import { useEffect, useRef, useState } from 'react';
import { t } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { get, DEALS_PATH } from './api';
import styles from './DealFloorWorking.module.css';

/**
 * The floor's working (piece 19). The Deal page prints the floor and no longer
 * prints the arithmetic under it — the product owner asked for the derivation
 * rows off the page on 2026-09-14 — so the working is asked for on demand by
 * whoever is allowed it, and the control that asks surfaces when the pointer is
 * over the General Information tile.
 *
 * 🔨 The withholding is the server's, not this component's: `offered` decides
 * whether a control is drawn, and `GET deals/:id/floor-derivation` refuses a
 * viewer outside the seeded Admin role, or one refused `Item.purchasePrice`,
 * whatever the browser asks. A row hidden in the browser is a row the server
 * still sent.
 */
export const FLOOR_DERIVATION_PATH = (dealId: string) => `${DEALS_PATH}/${dealId}/floor-derivation`;

export interface DealFloorWorkingProps {
  dealId: string;
  /** Whether this viewer is offered the working at all; the page's own facts answer it, and the route answers it again. */
  offered: boolean;
  apiBaseUrl?: string;
}

interface FloorWorkingAnswer {
  lines: string[];
  floorText: string;
}

export function DealFloorWorking({ dealId, offered, apiBaseUrl = '' }: DealFloorWorkingProps) {
  const anchor = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<readonly string[] | null>(null);
  const [failed, setFailed] = useState(false);

  /*
   * The tile the panel sits in is the hover target, so the control appears
   * wherever on General Information the pointer happens to be rather than only
   * over its own few pixels. The tile is found by the attribute the record page
   * already stamps on every tile's slot; no tile component is touched.
   */
  useEffect(() => {
    if (!offered) return undefined;
    const tile = anchor.current?.closest('[data-record-tile]') ?? null;
    if (!tile) return undefined;
    const enter = () => setNear(true);
    const leave = () => setNear(false);
    tile.addEventListener('pointerenter', enter);
    tile.addEventListener('pointerleave', leave);
    return () => {
      tile.removeEventListener('pointerenter', enter);
      tile.removeEventListener('pointerleave', leave);
    };
  }, [offered]);

  if (!offered) return null;

  const toggle = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (lines !== null) return;
    const answer = await get<FloorWorkingAnswer>(FLOOR_DERIVATION_PATH(dealId), apiBaseUrl);
    if (answer.ok) setLines(answer.data.lines ?? []);
    else setFailed(true);
  };

  const shown = lines ?? [];
  return (
    <div className={styles.panel} ref={anchor} data-floor-working={open ? 'open' : 'closed'}>
      <button
        type="button"
        className={`${styles.trigger} ${near || open ? styles.revealed : ''}`}
        onClick={() => void toggle()}
        aria-expanded={open}
        data-floor-working-toggle="true"
      >
        <Icon meaning={open ? 'collapse' : 'expand'} size="inline" />
        {open ? t('deal.floor.workingHide') : t('deal.floor.working')}
      </button>
      {open ? (
        <ul className={styles.working} data-floor-working-lines={String(shown.length)}>
          {failed ? <li className={styles.line}>{t('deal.floor.workingFailed')}</li> : null}
          {!failed && lines === null ? <li className={styles.line}>{t('deal.floor.workingLoading')}</li> : null}
          {shown.map((text, index) => (
            <li key={text} className={index === shown.length - 1 ? styles.total : styles.line}>
              {text}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
